import { ComputedBadge, OfficialBadge } from '@/components/Badge';
import { Formater } from '@/components/helpers/Financials/helpers';
import { ObjectListItemType } from '@/components/ObjectList/type';
import {
  ItemBoolType,
  ItemMapType,
  ItemStringType,
  ItemTrendType,
  Trend,
} from '@/components/Widget/types';
import biFilter from '@/features/helpers/bifilter';
import {
  formatFullInt,
  formatPercentToString,
  getYear,
  isNumber,
  roundNumber,
} from '@/features/helpers/numbers';
import Pia from '@/features/pia/type';
import { TFunction } from 'i18next';
import { ReactNode } from 'react';
import { calProps } from './Valuation';

export function formatAdmin({
  admin,
  type,
}: {
  admin?: Pia.Admin;
  type: 'lots' | 'buildings' | 'dwellings';
}) {
  switch (type) {
    case 'lots':
      return [
        {
          label: 'asset.description.lots.city',
          badge: <OfficialBadge useAbbreviation />,
          value: admin?.city,
        },
        {
          label: 'asset.description.lots.land_use',
          badge: <OfficialBadge useAbbreviation />,
          value: admin?.land_use,
        },
      ];
    case 'buildings':
      return [
        {
          label: 'asset.description.buildings.egid',
          badge: <OfficialBadge useAbbreviation />,
          value: admin?.egid?.toString(),
        },
        {
          label: 'asset.description.buildings.city',
          badge: <OfficialBadge useAbbreviation />,
          value: admin?.city,
        },
        {
          label: 'asset.description.buildings.official_nb',
          badge: <OfficialBadge useAbbreviation />,
          value: admin?.official_nb,
        },
        {
          label: 'asset.description.buildings.land_use',
          badge: <OfficialBadge useAbbreviation />,
          value: admin?.land_use,
        },
        {
          label: 'asset.description.buildings.status',
          badge: <OfficialBadge useAbbreviation />,
          value: admin?.status,
        },
        {
          label: 'asset.description.buildings.class',
          badge: <OfficialBadge useAbbreviation />,
          value: admin?.class,
        },
        {
          label: 'asset.description.buildings.category',
          badge: <OfficialBadge useAbbreviation />,
          value: admin?.category,
        },
      ];
    case 'dwellings':
      return [
        {
          label: 'asset.description.dwellings.ewid',
          badge: <OfficialBadge useAbbreviation />,
          value: admin?.ewid?.toString(),
        },
        {
          label: 'asset.description.dwellings.city',
          badge: <OfficialBadge useAbbreviation />,
          value: admin?.city,
        },
        {
          label: 'asset.description.dwellings.land_use',
          badge: <OfficialBadge useAbbreviation />,
          value: admin?.land_use,
        },
        {
          label: 'asset.description.dwellings.status',
          badge: <OfficialBadge useAbbreviation />,
          value: admin?.status,
        },
        {
          label: 'asset.description.dwellings.class',
          badge: <OfficialBadge useAbbreviation />,
          value: admin?.class,
        },
        {
          label: 'asset.description.dwellings.category',
          badge: <OfficialBadge useAbbreviation />,
          value: admin?.category,
        },
      ];
  }
}

export function formatPlotIdentification({
  description,
  admin,
  isLoading,
  t,
  marker,
  markerIsLoading,
}: {
  description?: Pia.GeneralDescription;
  admin?: Pia.Admin;
  isLoading: boolean;
  t: TFunction<'translation', undefined>;
  marker?: Pia.Marker;
  markerIsLoading: boolean;
}) {
  return [
    {
      title: t('asset_page.description.tile.description'),
      data: [
        {
          badge: <OfficialBadge useAbbreviation />,
          label: 'asset.description.lots.egrid',
          value: description?.egrid,
        },
        {
          badge: <OfficialBadge useAbbreviation />,
          label: 'asset.description.lots.municipality',
          value: description?.municipality,
        },
        {
          badge: <OfficialBadge useAbbreviation />,
          label: 'asset.description.lots.number',
          value: description?.number,
        },
        {
          badge: <OfficialBadge useAbbreviation />,
          label: 'asset.description.lots.sector',
          value: description?.sector?.toString(),
        },
        {
          badge: <OfficialBadge useAbbreviation />,
          label: 'asset.description.lots.surface',
          value: description?.surface,
          suffix: 'units.area',
        },
      ] as (ItemBoolType | ItemStringType)[],
      isLoading: isLoading,
      span: 2,
    },
    {
      title: t('asset_page.description.tile.usage'),
      data: [
        {
          label: 'asset.description.lots.land_use',
          value: admin?.land_use,
          badge: <OfficialBadge useAbbreviation />,
        },
        {
          label: 'asset.description.lots.built_up_area',
          value: formatFullInt(description?.built_up?.area),
          suffix: 'units.built_up.area',
          badge: <OfficialBadge useAbbreviation />,
        },
        {
          label: 'asset.description.lots.built_up_ratio',
          value: formatPercentToString(description?.built_up?.ratio || null),
          suffix: 'units.built_up.ratio',
          badge: <ComputedBadge useAbbreviation />,
        },
      ] as (ItemBoolType | ItemStringType)[],
      isLoading: isLoading,
      span: 2,
    },
    {
      title: t('asset_page.description.tile.location'),
      badges: [<OfficialBadge />],
      data: [
        {
          type: 'map',
          data: marker?.coordinates ? [marker] : undefined,
        },
      ] as ItemMapType[],
      isLoading: markerIsLoading,
    },
  ];
}

export function formatGeneralDescription({
  description,
  type,
  id,
  isParent,
}: {
  description?: Pia.GeneralDescription;
  type: 'lots' | 'buildings' | 'dwellings';
  id: number | string | undefined;
  isParent: boolean;
}) {
  switch (type) {
    case 'lots':
      return [
        {
          label: 'asset.description.lots.egrid',
          value: description?.egrid,
          link: isParent ? `/asset/lots/${id}` : undefined,
          badge: <OfficialBadge useAbbreviation />,
        },
        {
          label: 'asset.description.lots.municipality',
          value: description?.municipality,
          badge: <OfficialBadge useAbbreviation />,
        },
        {
          label: 'asset.description.lots.number',
          value: description?.number,
          badge: <OfficialBadge useAbbreviation />,
        },
        {
          label: 'asset.description.lots.sector',
          value: description?.sector?.toString(),
          badge: <OfficialBadge useAbbreviation />,
        },
        {
          label: 'asset.description.lots.surface',
          value: description?.surface,
          suffix: 'units.surface',
          badge: <OfficialBadge useAbbreviation />,
        },
        {
          label: 'asset.description.lots.built_up_area',
          value: formatFullInt(description?.built_up?.area || null),
          suffix: 'units.built_up.area',
          badge: <OfficialBadge useAbbreviation />,
        },
        {
          label: 'asset.description.lots.built_up_ratio',
          value: formatPercentToString(description?.built_up?.ratio || null),
          suffix: 'units.built_up.ratio',
          badge: <ComputedBadge useAbbreviation />,
        },
      ];
    case 'buildings': {
      // filter if parent. then dont show optional
      return biFilter(
        [
          {
            label: 'asset.description.dwellings.egid',
            value: description?.egid,
            link: `/asset/buildings/${id}`,
            optional: true,
            badge: <OfficialBadge useAbbreviation />,
          },
          {
            label: 'asset.description.buildings.construction_date',
            value: description?.construction_date
              ? getYear(description.construction_date)
              : undefined,
            badge: <OfficialBadge useAbbreviation />,
          },
          {
            label: 'asset.description.buildings.floors_nb',
            value: formatFullInt(description?.floors_nb),
            badge: <OfficialBadge useAbbreviation />,
          },
          {
            label: 'asset.description.buildings.dwellings_nb',
            value: formatFullInt(description?.dwellings_nb),
            badge: <OfficialBadge useAbbreviation />,
          },
        ],
        (item) => {
          if (!isParent && item.optional) return false;
          else return true;
        }
      )[0];
    }
    case 'dwellings': {
      return [
        {
          label: 'asset.description.buildings.construction_date',
          value: description?.construction_date
            ? getYear(description.construction_date)
            : undefined,
          badge: <OfficialBadge useAbbreviation />,
        },
        {
          label: 'asset.description.dwellings.rooms_nb',
          value: formatFullInt(description?.rooms_nb),
          badge: <OfficialBadge useAbbreviation />,
        },
        {
          label: 'asset.description.dwellings.floor',
          value: formatFullInt(description?.floor),
          badge: <OfficialBadge useAbbreviation />,
        },
        {
          label: 'asset.description.dwellings.surface',
          value: formatFullInt(description?.area),
          suffix: 'units.area',
          badge: <OfficialBadge useAbbreviation />,
        },
      ];
    }
  }
}

export function formatGeometry(
  description: Pia.GeneralDescription | undefined,
  type: 'lots' | 'buildings' | 'dwellings'
) {
  switch (type) {
    case 'lots':
      return [];
    case 'buildings':
      return [
        {
          label: 'asset.description.buildings.residential_area',
          value: formatFullInt(description?.residential_area),
          suffix: 'units.residential_area',
          badge: <OfficialBadge useAbbreviation />,
        },
        {
          label: 'asset.description.buildings.commercial_area',
          value: formatFullInt(description?.commercial_area),
          suffix: 'units.commercial_area',
          badge: <ComputedBadge useAbbreviation />,
        },
        {
          label: 'asset.description.buildings.total_area',
          value: formatFullInt(description?.total_area),
          suffix: 'units.total_area',
          style: 'bold',
          badge: <ComputedBadge useAbbreviation />,
        },
        {
          label: 'asset.description.buildings.area',
          value: formatFullInt(description?.area),
          suffix: 'units.area',
          badge: <OfficialBadge useAbbreviation />,
        },
        {
          label: 'asset.description.buildings.volume',
          value: formatFullInt(description?.volume),
          suffix: 'units.volume',
          badge: <OfficialBadge useAbbreviation />,
        },
      ];
    case 'dwellings':
      return [];
  }
}

export function formatCivilStatus(status: Pia.Status | undefined) {
  return [
    {
      label: 'asset_page.social.demographic.civil_status.household',
      value: status?.computed_household_mean_size
        ? formatFullInt(status?.computed_household_mean_size)
        : '-',
      badge: <ComputedBadge useAbbreviation />,
      suffix: 'units.household_size',
    },
    {
      label: 'asset_page.social.demographic.civil_status.marriage',
      value: formatPercentToString(status?.computed_marriage_rate ?? null),
      badge: <ComputedBadge useAbbreviation />,
      suffix: 'units.marriage',
    },
    {
      label: 'asset_page.social.demographic.civil_status.divorce',
      value: formatPercentToString(status?.computed_divorce_rate ?? null),
      badge: <ComputedBadge useAbbreviation />,
      suffix: 'units.divorce',
    },
  ];
}

export function formatDemographicAges(ages: Pia.Ages) {
  const formatAges = ages['ages-by-gender'];
  const preAges = Object.entries(formatAges).map(([key, val]) => {
    return {
      key: key.trim(),
      man: val.man ?? 0,
      woman: val.woman ?? 0,
    };
  });

  const maxPopulation = preAges.reduce((acc, { man, woman }) => acc + man + woman, 0);
  const data = preAges.map((item) => {
    return {
      key: item.key,
      value1: Number(((item.man as number) / maxPopulation) * 100),
      value2: Number(((item.woman as number) / maxPopulation) * 100),
    };
  });
  const sortData = data.sort(
    (group1, group2) =>
      Number(group2.key.split('-')[0].split('+')[0]) -
      Number(group1.key.split('-')[0].split('+')[0])
  );
  return { data: sortData, group1: 'man', group2: 'woman' };
}

export function formatDemographicGender(gender: Pia.Gender) {
  const preGender = gender?.gender?.map((item) => {
    return { group: item.group, value: item.value1 };
  });
  return preGender || [];
}
export function formatDemographicForeigners(foreigner: Pia.Foreigners | undefined) {
  if (!foreigner?.citizenship) return [];
  const preForeigner = foreigner.citizenship.map((item) => {
    return { group: item.group, value: item.value1 };
  });
  return preForeigner;
}

export function getLegendsFromOverview(overview: Pia.Overview): ObjectListItemType['legends'] {
  const legends = [
    { type: 'surface', value: overview.area ?? overview?.total_area },
    { type: 'volume', value: overview.volume },
    { type: 'lots_nb', value: overview.lots_nb },
    { type: 'buildings_nb', value: overview.buildings_nb },
    { type: 'dwellings_nb', value: overview.dwellings_nb },
  ];
  const filteredLegends = legends.filter(({ value }) =>
    Boolean(value)
  ) as ObjectListItemType['legends'];
  return filteredLegends;
}

export function getDescriptionFromOverview(overview: Pia.Overview): Array<ItemTrendType> {
  const beforeFilter = [
    {
      label: 'asset_page.overview.description.market_value',
      trends: [
        {
          value: overview.market_value ? formatFullInt(overview.market_value) : null,
          diff: Number(overview.market_value_var),
          suffix: 'units.market_value',
        },
      ],
    },

    {
      label: 'asset_page.overview.description.net_rent',
      trends: [
        {
          value: overview.net_rent ? formatFullInt(overview.net_rent) : null,
          diff: Number(overview.net_rent_var),
          suffix: 'units.net_rent',
        },
      ],
    },
    {
      label: 'asset_page.overview.description.charges',
      trends: [
        {
          value: overview.charges ? formatFullInt(overview.charges) : null,
          diff: Number(overview.charges_var),
          color: 'reverse' as Trend['color'],
          suffix: 'units.charges',
        }, //reverse
      ],
    },
    {
      label: 'asset_page.overview.description.roa',
      trends: [
        {
          value: overview.roa ? formatPercentToString(overview.roa) : null,
          diff: Number(overview.roa_var),
          suffix: 'units.roa',
        },
      ],
    },
    {
      label: 'asset_page.overview.description.co2',
      trends: [
        {
          value: overview.co2_emissions !== null ? formatFullInt(overview.co2_emissions) : null,
          diff: Number(null),
          color: 'reverse' as Trend['color'],
          suffix: 'units.co2_m2',
        },
      ], // reverse
    },
    {
      label: 'asset_page.overview.description.idc',
      trends: [
        {
          value: overview.idc !== null ? formatFullInt(overview.idc) : null,
          diff: Number(null),
          color: 'reverse' as Trend['color'],
          suffix: 'units.idc_m2',
        },
      ], //reverse
    },
  ];

  const filteredDescriptions = beforeFilter.filter(({ trends }) =>
    trends.every(({ value }) => value !== null)
  );
  return filteredDescriptions;
}

export function calTotal(inputs: calProps) {
  const {
    type,
    notaryFee,
    registrationFee,
    transfertFee,
    managerFee,
    otherFeesPercent,
    otherFeesPlain,
    bs,
    valuation,
    otherValuation,
    marketPrice,
    residentialIncome,
    commercialIncome,
    otherIncome,
    netRent,
    capRate,
  } = inputs;
  const totIncome =
    type === 'dwellings'
      ? netRent
      : (residentialIncome || 0) + (commercialIncome || 0) + (otherIncome || 0);

  const feesPercent =
    (bs || 0) * ((notaryFee || 0) + (registrationFee || 0) + (transfertFee || 0)) +
    (managerFee || 0) +
    (otherFeesPercent || 0);
  const priceAndPercentFees = (marketPrice || 0) * (1 + feesPercent / 100);
  const additionalFees = (otherFeesPlain || 0) + (valuation || 0) + (otherValuation || 0);
  const qhTotal = marketPrice ? priceAndPercentFees + additionalFees : undefined;
  const capRatePP = capRate ? (totIncome || 0) / (capRate / 100) : marketPrice;
  const capRateTotal =
    typeof capRatePP === 'number'
      ? (capRatePP || 0) * (1 + feesPercent / 100) + additionalFees
      : undefined;
  return {
    qhTotal: typeof qhTotal === 'number' ? Math.round(qhTotal) : undefined,
    capRatePP: typeof capRatePP === 'number' ? Math.round(capRatePP) : undefined,
    capRateTotal: typeof capRateTotal === 'number' ? Math.round(capRateTotal) : undefined,
  };
}

export function formatGradient<T extends unknown>(
  t: TFunction<'translation', undefined>,
  isLoading: boolean,
  contexts?: Record<string, Pia.Context>,
  values?: T,
  valueInPercent?: boolean
) {
  return function (
    tuple:
      | [string, string, keyof T, string | undefined]
      | [string, string, keyof T, string | undefined, string | undefined]
      | [string, string, keyof T, string | undefined, string | undefined, number | undefined]
      | [
          string,
          string,
          keyof T,
          string | undefined,
          string | undefined,
          number | undefined,
          ReactNode | undefined,
        ]
  ) {
    const [contextKey, subjectKey, valueKey, unitKey, suffixKey, sigFigs, badge] = tuple;
    const subject = t(subjectKey);
    const unit = unitKey ? t(unitKey) : undefined;
    const suffix = suffixKey ? t(suffixKey) : undefined;
    const context = contexts?.[contextKey];
    return {
      subject,
      unit,
      suffix,
      value: valueInPercent ? Number(values?.[valueKey]) * 100 : values?.[valueKey],
      min: context?.lower_bound,
      max: context?.upper_bound,
      quartiles: [context?.q1, context?.q2, context?.q3],
      isLoading,
      sigFigs,
      badge,
    };
  };
}

export type HighlightLineTuple<T> =
  | [
      string | undefined,
      string,
      keyof T,
      string | undefined,
      string | undefined,
      string | undefined,
      undefined | Formater,
      'pts' | '%' | 'permill_pts' | undefined,
      boolean | undefined,
      ReactNode | undefined,
      number | undefined,
    ]
  | [
      string | undefined,
      string,
      keyof T,
      string | undefined,
      string | undefined,
      string | undefined,
      undefined | Formater,
      'pts' | '%' | 'permill_pts' | undefined,
      boolean | undefined,
      ReactNode | undefined,
      number | undefined,
      keyof T | undefined,
    ];

export type HighlightLineDict<T> = {
  id: string;
  i18nKey: string;
  key: keyof T;
  displayLabelKey?: keyof T;
  descriptionKey: string;
  unitKey?: string;
  extra?: string;
  formatter?: Formater;
  suffix?: 'pts' | '%';
  invert?: boolean;
  badge?: ReactNode;
  decimals?: number;
};

export function formatHighLightGradient<T extends unknown>(
  t: TFunction<'translation', undefined>,
  isLoading: boolean,
  contexts?: Record<string, Pia.Context | undefined>,
  source?: T,
  benchmarkSource?: T
) {
  return function (tuple: HighlightLineTuple<T>) {
    const [
      contextKey = '',
      subjectKey,
      valueKey,
      descriptionKey,
      unitKey,
      suffixKey,
      formater,
      varUnits,
      invertColors,
      badge,
      sigFigs,
      displayValueKey,
    ] = tuple;
    const subject = t(subjectKey);
    const unit = unitKey ? t(unitKey) : undefined;
    const suffix = suffixKey ? t(suffixKey) : undefined;
    const context = contexts?.[contextKey];
    const currentValue = source?.[valueKey];
    const benchmarkValue = benchmarkSource?.[valueKey];
    const diff =
      benchmarkValue && currentValue && isNumber(benchmarkValue) && isNumber(currentValue)
        ? currentValue - benchmarkValue
        : undefined;

    const permillPtsDiff =
      varUnits === 'permill_pts' && diff && (roundNumber(diff, 5) as number) * 1000;
    const percentPtsDiff = varUnits === 'pts' && diff && (roundNumber(diff, 4) as number) * 100;
    const percentDiff =
      varUnits === '%' &&
      diff &&
      isNumber(benchmarkValue) &&
      (roundNumber(diff / benchmarkValue, 4) as number) * 100;
    const variation = permillPtsDiff || percentPtsDiff || percentDiff || undefined;

    return {
      subject,
      unit,
      suffix,
      displayLabel: displayValueKey
        ? source?.[displayValueKey]
          ? t(`highlight.label.category.${source[displayValueKey]}`)
          : '-'
        : undefined,
      benchmarkdisplayLabel:
        displayValueKey && benchmarkSource
          ? benchmarkSource?.[displayValueKey]
            ? t(`highlight.label.category.${benchmarkSource[displayValueKey]}`)
            : '-'
          : undefined,
      value: currentValue,
      benchmarkValue: benchmarkValue,
      min: context?.lower_bound,
      max: context?.upper_bound,
      quartiles: [context?.q1, context?.q2, context?.q3],
      isLoading,
      sigFigs,
      badge,
      formater: formater,
      description: descriptionKey ? t(descriptionKey) : undefined,
      variation:
        variation && varUnits
          ? {
              value: variation,
              invertColors,
              suffix: varUnits.split('_')[varUnits.split('_').length - 1],
            }
          : undefined,
      noGradient: context ? false : true,
    };
  };
}

export function formatHighLightGradientDict<T extends object>(
  t: TFunction<'translation', undefined>,
  isLoading: boolean,
  dict: HighlightLineDict<T>,
  contexts?: Record<string, Pia.Context | undefined>,
  data?: Partial<T>,
  benchmarkSource?: Partial<T>
) {
  const {
    id: contextKey,
    i18nKey: subjectKey,
    key: valueKey,
    descriptionKey,
    displayLabelKey,
    unitKey,
    suffix: suffixKey,
    formatter: formater,
    invert: invertColors,
    badge,
    decimals: sigFigs,
  } = dict;
  const subject = t(subjectKey);
  const unit = unitKey ? t(unitKey) : undefined;
  const suffix = suffixKey ? t(suffixKey) : undefined;
  const context = contexts?.[contextKey];
  const currentValue = valueKey ? data?.[valueKey] : undefined;
  const displayLabel = displayLabelKey ? data?.[displayLabelKey] : undefined;
  const benchmarkValue = valueKey ? benchmarkSource?.[valueKey] : undefined;
  const isNumberValue = (v: unknown): v is number => typeof v === 'number' && !isNaN(v);
  const diff =
    isNumberValue(benchmarkValue) && isNumberValue(currentValue)
      ? roundNumber((currentValue as number) - (benchmarkValue as number), 4)
      : undefined;
  const variation =
    suffixKey === 'pts'
      ? diff
        ? diff * 100
        : undefined
      : diff && isNumberValue(benchmarkValue) && (diff / (benchmarkValue as number)) * 100;

  return {
    subject,
    unit,
    suffix,
    value: currentValue as number | null | undefined,
    displayLabel: t(`highlight.label.category.${displayLabel}`),
    benchmarkValue: benchmarkValue as number | null | undefined,
    min: context?.lower_bound,
    max: context?.upper_bound,
    quartiles: [context?.q1, context?.q2, context?.q3],
    isLoading,
    sigFigs,
    badge,
    formater,
    description: descriptionKey ? t(descriptionKey) : undefined,
    variation:
      variation && suffixKey
        ? {
            value: variation,
            invertColors,
            suffix: suffixKey,
          }
        : undefined,
  };
}
